import type { ReactNode } from "react";
import { AlertCircle, Clock } from "lucide-react";
import { Notice } from "../../../components/ui/Notice";
import {
  readHikPin,
  formatHikUses,
  formatAccessDateTime,
  effectiveAccessEnd,
  shouldShowHikPinPanel,
  type HikPinFields,
} from "../hikPinStatus";

// SAM-307 / SAM-306 / SAM-426 — the Hik-Connect door PIN block of the invitation detail,
// and the small status chip of the invitations list. Every hik_* field is optional: with
// none of them the panel renders nothing and the chip renders nothing.

type Row = HikPinFields & { end_date?: string | null };

const Divider = () => (
  <div style={{ width: '100%', height: 1, background: 'var(--line-subtle)', margin: '6px 0' }} />
);

const Label = ({ children }: { children: ReactNode }) => (
  <h3 style={{ fontSize: 13, fontWeight: 500, margin: '0 0 1px 0', color: 'var(--ink-900)' }}>{children}</h3>
);

const attemptsText = (attempts: number | null) =>
  attempts !== null ? ` ${attempts === 1 ? '1 attempt' : `${attempts} attempts`} so far.` : '';

interface PanelProps {
  row: Row | null | undefined;
  /**
   * Right after creating an invitation the block appears only when the backend sent a
   * status, so a pre-SAM-307 response keeps today's confirmation screen unchanged.
   */
  requireStatus?: boolean;
}

export const HikPinPanel = ({ row, requireStatus = false }: PanelProps) => {
  const info = readHikPin(row);
  if (!shouldShowHikPinPanel(info, requireStatus)) return null;

  const uses = formatHikUses(info);
  const validUntil = formatAccessDateTime(effectiveAccessEnd(row));
  const showPin = info.status !== 'pending' && info.status !== 'failed' && !!info.pin;

  return (
    <>
      <Divider />
      <div className="flex flex-col gap-2">
        <Label>Access PIN</Label>

        {info.status === 'pending' && (
          <div className="flex items-center gap-2" style={{ fontSize: 14, color: 'var(--warn-fg)' }}>
            <Clock size={14} />
            <span>Generating door PIN...</span>
            <span style={{ fontSize: 11, color: 'var(--ink-400)', background: 'var(--surface)', padding: '2px 8px', borderRadius: 'var(--r-6)' }}>
              Hik-Connect
            </span>
          </div>
        )}

        {info.status === 'failed' && (
          <Notice variant="error">
            <strong>Door PIN could not be generated.</strong>
            {info.error ? ` ${info.error}.` : ''}
            {attemptsText(info.attempts)} The guest cannot open the door with a PIN until it is generated.
          </Notice>
        )}

        {showPin && (
          <div className="flex items-center gap-2">
            <span style={{ fontFamily: 'var(--mono)', fontSize: 20, fontWeight: 700, letterSpacing: 4, color: 'var(--ink-900)' }}>
              {info.pin}
            </span>
            <span style={{ fontSize: 11, color: 'var(--ink-400)', background: 'var(--surface)', padding: '2px 8px', borderRadius: 'var(--r-6)' }}>
              Hik-Connect
            </span>
          </div>
        )}

        {(validUntil || uses) && (
          <div className="flex flex-col gap-1">
            {validUntil && (
              <div className="flex justify-between items-center gap-3">
                <p style={{ fontSize: 14, margin: 0, color: 'var(--ink-500)', fontWeight: 500 }}>Valid until</p>
                <p style={{ fontSize: 14, margin: 0, color: 'var(--ink-500)', textAlign: 'right' }}>{validUntil}</p>
              </div>
            )}
            {uses && (
              <div className="flex justify-between items-center gap-3">
                <p style={{ fontSize: 14, margin: 0, color: 'var(--ink-500)', fontWeight: 500 }}>Uses</p>
                <p style={{ fontSize: 14, margin: 0, color: 'var(--ink-500)', textAlign: 'right' }}>{uses}</p>
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
};

/** List chip: only for pending / failed. ok, skipped and absent render nothing. */
export const HikPinStatusChip = ({ row }: { row: HikPinFields | null | undefined }) => {
  const info = readHikPin(row);
  if (info.status !== 'pending' && info.status !== 'failed') return null;

  const failed = info.status === 'failed';
  const title = failed
    ? `Door PIN failed${info.error ? `: ${info.error}` : ''}${info.attempts !== null ? ` (${info.attempts} attempts)` : ''}`
    : 'Door PIN is being generated';

  return (
    <span
      title={title}
      className="inline-flex items-center gap-1"
      style={{
        fontSize: 11,
        fontWeight: 500,
        whiteSpace: 'nowrap',
        padding: '1px 8px',
        borderRadius: 'var(--r-pill)',
        color: failed ? 'var(--bad-fg)' : 'var(--warn-fg)',
        background: failed ? 'var(--bad-bg)' : 'var(--warn-bg)',
      }}
    >
      {failed ? <AlertCircle size={11} /> : <Clock size={11} />}
      {failed ? 'PIN failed' : 'PIN pending'}
    </span>
  );
};
